import SinglePropTracker from "../../../../prop-trackers/single-prop-tracker.js";
import PropTracker from "../../../../prop-trackers/prop-tracker.js";
import { Combiner } from "../../../../prop-tracker-utils/combiner.js";
import DeepValueMerger from "../../../../utils/deep-value-merger.js";

const CameraParams = ["position", "target", "fov", "zoom", "near", "far"];

const DefaultCameraData = {
    "position": { x: 2.5, y: 0, z: 2.5 },
    "target": { x: 0, y: 0, z: 0 },
    "fov": 45,
    "zoom": 1,
    "near": 0.1,
    "far": 10000,
};

class CameraTrackerController {
    constructor(wrapCtx, evtCtx, cameraController) {
        this._evtCtx = evtCtx;
        this._cameraController = cameraController;

        const defaultData = wrapCtx.getWrapper(DefaultCameraData);
        const defaultEvents = evtCtx.getEvents(defaultData);

        const mergedData = wrapCtx.getWrapper({});
        const mergedEvents = evtCtx.getEvents(mergedData);

        this._defaultData = defaultData;
        this._defaultEvents = defaultEvents;
        this._mergedData = mergedData;
        this._mergedEvents = mergedEvents;

        this._mergerSrcDefaultTracker = new PropTracker();
        this._mergerSrcSlideTracker = new PropTracker();
        this._merger = new Combiner((key) => new DeepValueMerger(wrapCtx, evtCtx));
        this._merger.setSource(1, this._mergerSrcDefaultTracker);
        this._merger.setSource(0, this._mergerSrcSlideTracker);

        this._paramTrackers = {
            position: new SinglePropTracker(),
            target: new SinglePropTracker(),
            fov: new SinglePropTracker(),
            zoom: new SinglePropTracker(),
            near: new SinglePropTracker(),
            far: new SinglePropTracker(),
        };

        this._positionTracker = new PropTracker();
        this._targetTracker = new PropTracker();
    }

    track(cameraData, cameraEvents) {
        if (cameraData) {
            this._mergerSrcDefaultTracker.track(this._defaultData, this._defaultEvents);
            this._mergerSrcSlideTracker.track(cameraData, cameraEvents);

            for (const param in this._paramTrackers) {
                const paramTracker = this._paramTrackers[param];
                paramTracker.track(this._mergedData, this._mergedEvents, param);
            }
        } else {
            this._mergerSrcDefaultTracker.track();
            this._mergerSrcSlideTracker.track();

            for (const param in this._paramTrackers) {
                const paramTracker = this._paramTrackers[param];
                paramTracker.track();
            }
        }
    }

    update() {
        this._merger.updateIfNeeded(({ props }) => {
            for (const { key, newExists, newValue } of props.iterateModified()) {
                if (newExists) {
                    this._mergedData[key] = newValue;
                } else {
                    delete this._mergedData[key];
                }
            }
        });

        this._paramTrackers.position.updateIfNeeded(({ prop: { newValue } }) => {
            this._positionTracker.track(newValue, newValue ? this._evtCtx.getEvents(newValue) : undefined);
        });

        this._paramTrackers.target.updateIfNeeded(({ prop: { newValue } }) => {
            this._targetTracker.track(newValue, newValue ? this._evtCtx.getEvents(newValue) : undefined);
        });

        this._paramTrackers.fov.updateIfNeeded(({ prop: { newExists, newValue } }) => {
            if (newExists) {
                this._cameraController.setFov(newValue);
            }
        });

        this._paramTrackers.zoom.updateIfNeeded(({ prop: { newExists, newValue } }) => {
            if (newExists) {
                this._cameraController.setZoom(newValue);
            }
        });

        this._paramTrackers.near.updateIfNeeded(({ prop: { newExists, newValue } }) => {
            if (newExists) {
                this._cameraController.setNear(newValue);
            }
        });

        this._paramTrackers.far.updateIfNeeded(({ prop: { newExists, newValue } }) => {
            if (newExists) {
                this._cameraController.setFar(newValue);
            }
        });

        this._positionTracker.updateIfNeeded(({ props }) => {
            const { newExists: xExists, newValue: x } = props.get("x");
            const { newExists: yExists, newValue: y } = props.get("y");
            const { newExists: zExists, newValue: z } = props.get("z");

            if (xExists && yExists && zExists) {
                this._cameraController.setPosition(x, y, z);
            }
        });

        this._targetTracker.updateIfNeeded(({ props }) => {
            const { newExists: xExists, newValue: x } = props.get("x");
            const { newExists: yExists, newValue: y } = props.get("y");
            const { newExists: zExists, newValue: z } = props.get("z");

            if (xExists && yExists && zExists) {
                this._cameraController.setTarget(x, y, z);
            }
        });
    }
}

export default CameraTrackerController;
